import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { normalizeMessages } from '../../utils/offerNormalize';
import { LIVE_POLL, livePollInterval, LIVE_STALE_MS } from '../../config/liveSync';
import { adminKeys } from '../../lib/queryKeys';

const defaultQueryOptions = {
  staleTime: LIVE_STALE_MS,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

export function useAdminMessagingConversations(enabled = true) {
  return useQuery({
    queryKey: adminKeys.messaging.conversations(),
    queryFn: async () => {
      const res = await api.get('/admin/messaging/conversations');
      return res.data.data || [];
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.BADGE),
    placeholderData: (previous) => previous,
  });
}

export function useAdminMessagingSellers(enabled = true) {
  return useQuery({
    queryKey: adminKeys.messaging.sellers(),
    queryFn: async () => {
      const res = await api.get('/admin/messaging/sellers');
      return res.data.data || [];
    },
    enabled,
    staleTime: LIVE_POLL.SETTINGS,
    refetchInterval: () => livePollInterval(LIVE_POLL.SETTINGS),
    placeholderData: (previous) => previous,
  });
}

export function useAdminConversationMessages(
  conversationId: string | null,
  enabled = true
) {
  return useQuery({
    queryKey: adminKeys.messaging.messages(conversationId || ''),
    queryFn: async () => {
      if (!conversationId) {
        return { messages: [], sellerUserId: null as string | null };
      }
      const res = await api.get(
        `/admin/messaging/conversations/${conversationId}/messages`
      );
      const raw = res.data.data?.messages || [];
      const conv = res.data.data?.conversation;
      const suId = conv?.sellerUser?.id;
      return {
        messages: normalizeMessages(Array.isArray(raw) ? raw : []) as unknown[],
        sellerUserId: suId ? String(suId) : null,
      };
    },
    enabled: enabled && !!conversationId,
    staleTime: 2_000,
    refetchInterval: () =>
      conversationId ? livePollInterval(LIVE_POLL.CHAT) : false,
    placeholderData: (previous) => previous,
  });
}

export function useAdminEmailBroadcasts(enabled = true) {
  return useQuery({
    queryKey: adminKeys.messaging.emailBroadcasts(),
    queryFn: async () => {
      const res = await api.get('/admin/messaging/email/broadcasts');
      return res.data.data || [];
    },
    enabled,
    ...defaultQueryOptions,
    refetchInterval: () => livePollInterval(LIVE_POLL.LIST),
    placeholderData: (previous) => previous,
  });
}

export function useAdminEmailBroadcastDetail(broadcastId: string | null, enabled = true) {
  return useQuery({
    queryKey: adminKeys.messaging.emailBroadcast(broadcastId || ''),
    queryFn: async () => {
      if (!broadcastId) return null;
      const res = await api.get(`/admin/messaging/email/broadcasts/${broadcastId}`);
      return res.data.data || null;
    },
    enabled: enabled && !!broadcastId,
    ...defaultQueryOptions,
    refetchInterval: () =>
      broadcastId ? livePollInterval(LIVE_POLL.LIST) : false,
    placeholderData: (previous) => previous,
  });
}

export function useAdminMessagingInvalidation() {
  const queryClient = useQueryClient();
  return {
    invalidateConversations: () =>
      queryClient.invalidateQueries({ queryKey: adminKeys.messaging.conversations() }),
    invalidateMessages: () =>
      queryClient.invalidateQueries({ queryKey: adminKeys.messaging.messagesPrefix() }),
    invalidateEmails: () =>
      queryClient.invalidateQueries({ queryKey: adminKeys.messaging.emailPrefix() }),
    queryClient,
  };
}
